import React, { useEffect, useState } from 'react';

import { GoogleMap, InfoWindow, Marker } from '@react-google-maps/api';

import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';

import { ExperienceOfferRelatedLocation } from '~/services/ExperiencesService';

const MAP_CONTAINER_STYLE = { width: '100%', height: '400px' };
const MAX_ZOOM = 16;

interface Props {
  locations: ExperienceOfferRelatedLocation[];
  offerLocation?: { latitude: number; longitude: number };
  selectedLocationId: string | null;
  onSelectLocation: (locationId: string | null) => void;
}

export default function RelatedLocationsMap({ locations, offerLocation, selectedLocationId, onSelectLocation }: Props) {
  const theme = useTheme();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const selectedLocation = locations.find((location) => location.id === selectedLocationId);
  const selectedIndex = locations.findIndex((location) => location.id === selectedLocationId);

  useEffect(() => {
    if (!map) return;

    const bounds = new google.maps.LatLngBounds();
    locations.forEach((location) => bounds.extend({ lat: location.latitude, lng: location.longitude }));
    if (offerLocation) bounds.extend({ lat: offerLocation.latitude, lng: offerLocation.longitude });
    if (!bounds.isEmpty()) map.fitBounds(bounds, 50);
  }, [map, locations, offerLocation]);

  useEffect(() => {
    if (map && selectedLocation) {
      map.panTo({ lat: selectedLocation.latitude, lng: selectedLocation.longitude });
    }
  }, [map, selectedLocation]);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        options={{ maxZoom: MAX_ZOOM, streetViewControl: false, fullscreenControl: true }}
        onLoad={setMap}
        onUnmount={() => setMap(null)}
      >
        {offerLocation && (
          <Marker
            position={{ lat: offerLocation.latitude, lng: offerLocation.longitude }}
            title="Offer location"
            label="O"
          />
        )}
        {locations.map((location, index) => (
          <Marker
            key={location.id}
            position={{ lat: location.latitude, lng: location.longitude }}
            title={`Point ${index + 1}: ${location.excluded ? 'Excluded' : 'Included'}`}
            label={{ text: String(index + 1), color: theme.palette.primary.contrastText }}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: location.excluded ? theme.palette.text.disabled : theme.palette.primary.main,
              fillOpacity: 1,
              strokeColor: theme.palette.background.paper,
              strokeWeight: location.id === selectedLocationId ? 3 : 1,
              scale: location.id === selectedLocationId ? 16 : 12,
            }}
            onClick={() => onSelectLocation(location.id)}
          />
        ))}
        {selectedLocation && (
          <InfoWindow
            position={{ lat: selectedLocation.latitude, lng: selectedLocation.longitude }}
            onCloseClick={() => onSelectLocation(null)}
          >
            <Stack spacing={1}>
              <Typography variant="subtitle2">Point {selectedIndex + 1}</Typography>
              <Chip label={selectedLocation.excluded ? 'Excluded' : 'Included'} size="small" />
              <Typography variant="body2">
                {selectedLocation.latitude}, {selectedLocation.longitude}
              </Typography>
            </Stack>
          </InfoWindow>
        )}
      </GoogleMap>
    </Box>
  );
}
